/**
 * Parse and format email body for display
 * Handles multipart emails, HTML, plain text, and base64 encoded content
 * @param {string} body - Raw email body
 * @param {object} options - Options for parsing
 * @returns {object} - { html: string, preview: string, isHtml: boolean }
 */
export function parseEmailBody(body, options = {}) {
  if (!body) {
    return { html: "", preview: "", isHtml: false };
  }

  const { preferHtml = true } = options;
  let html = "";
  let preview = "";
  let isHtml = false;

  /**
   * Extract plain text from HTML or mixed content
   */
  function extractPlainText(htmlText) {
    if (!htmlText) return "";
    
    // Remove HTML tags
    let text = htmlText.replace(/<[^>]*>/g, "");
    
    // Decode HTML entities
    text = text
      .replace(/&nbsp;/g, " ")
      .replace(/&amp;/g, "&")
      .replace(/&lt;/g, "<")
      .replace(/&gt;/g, ">")
      .replace(/&quot;/g, '"')
      .replace(/&#39;/g, "'")
      .replace(/&apos;/g, "'")
      .replace(/&#x([0-9A-Fa-f]+);/g, (match, hex) => {
        return String.fromCharCode(parseInt(hex, 16));
      })
      .replace(/&#(\d+);/g, (match, dec) => {
        return String.fromCharCode(parseInt(dec, 10));
      });
    
    // Clean up whitespace
    text = text
      .replace(/\s+/g, " ") // Multiple spaces to single space
      .replace(/\n\s*\n/g, "\n") // Multiple newlines to single
      .trim();
    
    return text;
  }

  // Check if body contains multipart boundaries
  const multipartBoundary = body.match(/boundary="?([^"\s]+)"?/i) || 
                              body.match(/boundary=([^\s;]+)/i);
  
  if (multipartBoundary) {
    const boundary = multipartBoundary[1];
    const parts = body.split(`--${boundary}`);
    
    let htmlPart = null;
    let textPart = null;
    
    for (const part of parts) {
      // Skip empty parts and closing boundary
      if (!part.trim() || part.trim() === "--") continue;
      
      // Check content type
      const contentType = part.match(/Content-Type:\s*([^;\r\n]+)/i);
      const isHtmlContent = contentType && contentType[1].toLowerCase().includes("text/html");
      const isTextContent = contentType && contentType[1].toLowerCase().includes("text/plain");
      
      // Check for content transfer encoding
      const encoding = part.match(/Content-Transfer-Encoding:\s*([^\r\n]+)/i);
      const isBase64 = encoding && encoding[1].toLowerCase().includes("base64");
      
      // Extract the actual content (after headers)
      const contentMatch = part.match(/\r?\n\r?\n([\s\S]*)/);
      if (!contentMatch) continue;
      
      let content = contentMatch[1].trim();
      
      // Decode base64 if needed (in browser, use atob)
      if (isBase64) {
        try {
          // Remove any whitespace from base64 string
          content = content.replace(/\s/g, "");
          // Decode base64
          const binaryString = atob(content);
          // Convert to UTF-8
          content = decodeURIComponent(
            escape(binaryString)
          );
        } catch (e) {
          console.error("Error decoding base64:", e);
          continue;
        }
      }
      
      if (isHtmlContent && !htmlPart) {
        htmlPart = content;
      } else if (isTextContent && !textPart) {
        textPart = content;
      }
    }
    
    // Prefer HTML if available, otherwise use plain text
    if (preferHtml && htmlPart) {
      html = htmlPart;
      isHtml = true;
      preview = extractPlainText(htmlPart);
    } else if (textPart) {
      html = textPart.replace(/\n/g, "<br>");
      preview = textPart;
      isHtml = false;
    } else if (htmlPart) {
      html = htmlPart;
      isHtml = true;
      preview = extractPlainText(htmlPart);
    }
  } else {
    // Not multipart or parsing failed - check if it's HTML or plain text
    const trimmedBody = body.trim();
    
    // Remove common email artifacts
    let cleanedBody = trimmedBody
      .replace(/^[\s\S]*?Content-Type:[\s\S]*?\r?\n\r?\n/i, "") // Remove headers if present
      .replace(/--[A-Za-z0-9'()+_,\-.\/:=?]+\r?\n?/g, "") // Remove boundary markers
      .replace(/Content-[^:]+:[^\r\n]+\r?\n/gi, "") // Remove content headers
      .trim();
    
    // Check if body looks like HTML
    if (cleanedBody.match(/<[a-z][\s\S]*>/i)) {
      html = cleanedBody;
      isHtml = true;
      preview = extractPlainText(cleanedBody);
    } else {
      // Plain text - clean up any remaining artifacts
      cleanedBody = cleanedBody
        .replace(/[-_=]{20,}/g, " ") // Remove boundary-like sequences
        .replace(/[A-Za-z0-9_-]{50,}/g, (match) => {
          // Truncate very long strings that look like encoded data
          return match.length > 100 ? match.substring(0, 50) + "..." : match;
        });
      
      html = cleanedBody.replace(/\n/g, "<br>");
      preview = cleanedBody;
      isHtml = false;
    }
  }
  
  // Clean up preview - remove long strings that look like boundaries or encoded data
  if (preview) {
    preview = preview
      .replace(/[-_=]{20,}/g, " ") // Remove long sequences of dashes/underscores/equals (boundaries)
      .replace(/[A-Za-z0-9_-]{50,}/g, (match) => {
        // If it's a very long alphanumeric string, it might be encoded data
        return match.length > 100 ? match.substring(0, 50) + "..." : match;
      })
      .replace(/\s+/g, " ")
      .trim();
    
    // Limit preview length
    if (preview.length > 200) {
      preview = preview.substring(0, 200).trim() + "...";
    }
  }
  
  return {
    html: html || "",
    preview: preview || "",
    isHtml: isHtml,
  };
}

